<script setup lang="ts">
import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import {
  shiftVersionsApi,
  type ShiftVersion,
  type ShiftVersionChange,
} from '@/api/shift-versions';

const props = defineProps<{
  shiftId: number;
}>();

const { data: versions, isPending } = useQuery({
  queryKey: ['shiftVersions', props.shiftId],
  queryFn: () => shiftVersionsApi.index(props.shiftId),
});

const PEOPLE_FIELDS = new Set(['patient_id', 'caregiver_id']);
const SCHEDULE_FIELDS = new Set(['start_datetime', 'end_datetime']);
const FINANCIAL_FIELDS = new Set(['price', 'caregiver_cost', 'with_tax_bill']);

interface ChangeGroup {
  type: 'people' | 'schedule' | 'financial';
  changes: ShiftVersionChange[];
}

function groupChanges(changes: ShiftVersionChange[]): ChangeGroup[] {
  const groups: [ChangeGroup['type'], ShiftVersionChange[]][] = [
    ['people', changes.filter(c => PEOPLE_FIELDS.has(c.field))],
    ['schedule', changes.filter(c => SCHEDULE_FIELDS.has(c.field))],
    ['financial', changes.filter(c => FINANCIAL_FIELDS.has(c.field))],
  ];

  return groups
    .filter(([, g]) => g.length > 0)
    .map(([type, g]) => ({ type, changes: g }));
}

const groupedVersions = computed(() =>
  (versions.value || []).map(v => ({
    ...v,
    groups: groupChanges(v.changes),
  })),
);

function eventLabel(event: ShiftVersion['event']) {
  switch (event) {
  case 'create': return 'Creado';
  case 'update': return 'Actualizado';
  case 'destroy': return 'Eliminado';
  default: return event;
  }
}

function eventColor(event: ShiftVersion['event']) {
  switch (event) {
  case 'create': return 'success';
  case 'update': return 'info';
  case 'destroy': return 'error';
  default: return 'grey';
  }
}
</script>
<template>
  <div class="pa-4">
    <div
      v-if="isPending"
      class="d-flex justify-center pa-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>
    <div
      v-else-if="groupedVersions.length === 0"
      class="text-center text-grey pa-8"
    >
      <v-icon
        size="48"
        class="mb-4"
      >
        mdi-history
      </v-icon>
      <div>No hay cambios registrados para este turno.</div>
    </div>
    <div
      v-else
      class="timeline-container"
    >
      <div
        v-for="(version, index) in groupedVersions"
        :key="version.id"
        class="mb-3 timeline-item"
      >
        <div class="d-flex align-start">
          <div class="mt-1 timeline-dot me-3">
            <v-icon
              size="12"
              :color="eventColor(version.event)"
            >
              mdi-circle
            </v-icon>
          </div>
          <div class="timeline-content flex-grow-1">
            <div class="mb-1 d-flex align-center ga-2">
              <v-chip
                :color="eventColor(version.event)"
                size="small"
                label
              >
                {{ eventLabel(version.event) }}
              </v-chip>
              <span class="text-body-2 text-medium-emphasis">
                {{ version.createdAt }}
              </span>
              <span
                v-if="version.whodunnitLabel"
                class="text-body-2 text-medium-emphasis"
              >
                — {{ version.whodunnitLabel }}
              </span>
            </div>
            <div class="timeline-text">
              <div class="columns-container">
                <div
                  v-for="(group, gi) in version.groups"
                  :key="gi"
                  :class="['column-group', `column-group--${group.type}`]"
                >
                  <div
                    v-for="change in group.changes"
                    :key="change.field"
                    class="change-row"
                  >
                    <span class="font-weight-medium text-medium-emphasis">
                      {{ change.fieldLabel }}
                    </span>
                    <span v-if="version.event === 'create'">
                      {{ change.newValue }}
                    </span>
                    <span v-else-if="version.event === 'destroy'">
                      {{ change.oldValue }}
                    </span>
                    <span
                      v-else
                      class="d-inline-flex align-center"
                    >
                      <span class="text-medium-emphasis">
                        {{ change.oldValue }}
                      </span>
                      <v-icon
                        size="14"
                        class="mx-1"
                      >
                        mdi-arrow-right
                      </v-icon>
                      <span class="font-weight-medium">
                        {{ change.newValue }}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <v-divider
          v-if="index < groupedVersions.length - 1"
          class="mt-3"
        />
      </div>
    </div>
  </div>
</template>
<style scoped>
.timeline-container {
  position: relative;
}

.timeline-item {
  position: relative;
}

.timeline-dot {
  position: relative;
  z-index: 1;
}

.timeline-item:not(:last-child) .timeline-dot::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 16px;
  width: 2px;
  height: 40px;
  background-color: rgb(var(--v-theme-primary), .3);
  transform: translateX(-50%);
}

.timeline-content {
  position: relative;
}

.timeline-text {
  padding: 4px 0 4px 12px;
  border-left: 3px solid rgb(var(--v-theme-primary));
}

.columns-container {
  display: flex;
  gap: 24px;
  font-size: .875rem;
  flex-wrap: wrap;
  align-items: start;
}

.column-group {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2px 10px;
  align-items: baseline;
  white-space: nowrap;
}

.column-group--people {
  max-width: 280px;
  white-space: normal;
}

.change-row {
  display: contents;
}
</style>
